import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { useBundle } from '@amzn/react-arb-tools'
import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'

import { DIRECT_LINK_TABS } from '@components/BottomNav/BottomNavBar'
import ToolIcon from '@components/BottomNav/ToolIcon'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { authService } from '@services/auth/AuthService'
import { getToolNameToDetailMap } from '@services/ToolMenuConfigService'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { bottomNavConfig } from '@utils/ui-config'

import './BottomNav.scss'

interface IStoreToolsPageProps {
    open: boolean
    /** Subzone -> tool display names, already filtered by entitlement. */
    subzoneToToolMap: Map<string, string[]>
    displayNameOverrides?: Map<string, string>
    toggleMenu: () => void
    toggleMenuButton: () => void
}

/**
 * A tool the bottom nav links to directly gets no tile, so there is one way in rather than two.
 * Derived from DIRECT_LINK_TABS so adding a tab cannot leave a duplicate tile behind.
 */
export const TOOLS_EXCLUDED_FROM_GRID = DIRECT_LINK_TABS.map((tab) => tab.displayName)

/** Fixed, not viewport-derived: a wider phone must not become a four-column grid. */
export const TILES_PER_ROW = 3

/**
 * Splits into fixed-size rows, padding the last with nulls -- `widths="fill"` would
 * otherwise stretch the tiles of a short final row.
 */
export const chunkIntoRows = (toolNames: string[]): Array<Array<string | null>> => {
    const rows: Array<Array<string | null>> = []
    for (let i = 0; i < toolNames.length; i += TILES_PER_ROW) {
        const row: Array<string | null> = toolNames.slice(i, i + TILES_PER_ROW)
        while (row.length < TILES_PER_ROW) {
            row.push(null)
        }
        rows.push(row)
    }
    return rows
}

export const STORE_SWITCHER_SELECTOR = '.store-switcher-header-container'

/**
 * Viewport offset the grid has to start at: the bottom of the store switcher header, which sits
 * in flow under the nav bar. Measured rather than summed from config because the total is not
 * knowable here -- the nav bar is 3.7em, the switcher 40px, and an extra username line adds
 * another 25px via --email-header-offset. Four stylesheets already hardcode their own disagreeing
 * versions of that sum (95px, 105px, 110px); this adds no fifth.
 *
 * Returns null when the switcher is absent or hidden (display:none yields an all-zero rect), and
 * also when the rect puts it off the top of the viewport -- a fixed grid started there would cover
 * the chrome. In both cases the caller leaves `top` to BottomNav.scss, which clears the whole
 * chrome rather than the nav bar alone.
 */
export const measureContentTop = (): string | null => {
    const bottom = document.querySelector(STORE_SWITCHER_SELECTOR)?.getBoundingClientRect().bottom
    return bottom && bottom > 0 ? `${bottom}px` : null
}

export const WELCOME_BACK = 'jsma-web-storeTools-welcomeBack'

/**
 * Login id for the greeting. Not a given name: on React Native getUserProfile() returns the
 * shell-provided email as the name, so a BYOD device has no first name to show. An email is
 * reduced to its local part, minus any +suffix.
 */
export const toDisplayName = (name: string): string => name.split('@')[0].split('+')[0]

/** De-duplicated tool names in registration order, minus TOOLS_EXCLUDED_FROM_GRID. */
export const getAllToolNames = (subzoneToToolMap: Map<string, string[]>): string[] => {
    const toolNames: string[] = []
    subzoneToToolMap.forEach((tools) => {
        tools.forEach((tool) => {
            if (!toolNames.includes(tool) && !TOOLS_EXCLUDED_FROM_GRID.includes(tool)) {
                toolNames.push(tool)
            }
        })
    })
    return toolNames
}

/**
 * Full-screen grid of tool tiles, opened from the bottom nav's tools tab. The outer
 * div carries the fixed positioning Meridian has no prop for.
 */
const StoreToolsPage = ({
    open,
    subzoneToToolMap,
    displayNameOverrides,
    toggleMenu,
    toggleMenuButton,
}: Readonly<IStoreToolsPageProps>): React.ReactElement | null => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const toolNames = useMemo(() => getAllToolNames(subzoneToToolMap), [subzoneToToolMap])
    const toolRows = useMemo(() => chunkIntoRows(toolNames), [toolNames])
    /* Tracked for as long as the grid is open rather than sampled once when it opens. The
       switcher's viewport position depends on state this component never sees: the masthead grows
       by --email-header-offset when the shell supplies an email, a tool can hide the masthead
       altogether, and the network banner above it appears and disappears on its own. A frame loop
       rather than a ResizeObserver on the switcher because most of those move the switcher without
       resizing it, which an observer on it does not see. One rect read per frame, and only while a
       full-screen overlay is up. */
    const [contentTop, setContentTop] = useState<string | null>(null)
    useLayoutEffect(() => {
        if (!open) {
            setContentTop(null)
            return undefined
        }
        let frame = 0
        const track = () => {
            setContentTop(measureContentTop())
            frame = requestAnimationFrame(track)
        }
        track()
        return () => cancelAnimationFrame(frame)
    }, [open])

    const [userName, setUserName] = useState('')
    useEffect(() => {
        let cancelled = false
        authService.getUserProfile()
            .then(({ name }) => { if (!cancelled) setUserName(toDisplayName(name)) })
            .catch((err) => console.warn('[StoreToolsPage] Failed to resolve user profile:', err))
        return () => { cancelled = true }
    }, [])

    if (!open) {
        return null
    }

    const toolNameToDetailMap = getToolNameToDetailMap()

    return (
        <div
            className="store-tools-page"
            data-atb-id="store-tools-page"
            style={{
                // Unmeasured leaves BottomNav.scss's `top` in place; see measureContentTop.
                ...(contentTop ? { top: contentTop } : {}),
                bottom: bottomNavConfig.BOTTOM_NAV_HEIGHT,
            }}
        >
            {/* Inset order is CSS-like: top, right, bottom, left. The 200 below the greeting
                stacks with the 300 of top padding each tile already carries. */}
            <Column
                height="100%"
                overflowY="auto"
                backgroundColor="primary"
                spacing="200"
                spacingInset="400 400 400 400"
            >
                {userName && (
                    <Text type="h300" data-atb-id="store-tools-greeting">
                        {t(bundle, WELCOME_BACK, { username: userName })}
                    </Text>
                )}
                <Column spacing="300">
                    {toolRows.map((row, rowIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Row key={`storeToolsRow-${rowIndex}`} spacing="300" widths="fill" alignmentVertical="top">
                            {row.map((toolName, columnIndex) => {
                                // Padding cell in the final row; see chunkIntoRows.
                                if (!toolName) {
                                    // eslint-disable-next-line react/no-array-index-key
                                    return <div key={`storeToolsSpacer-${rowIndex}-${columnIndex}`} />
                                }
                                const tool = toolNameToDetailMap.get(toolName)
                                const label = displayNameOverrides?.get(toolName)
                                    || t(bundle, tool?.translateID)
                                    || tool?.displayName
                                    || toolName
                                return (
                                    <button
                                        key={`storeTools-${toolName}`}
                                        className="store-tools-tile"
                                        data-atb-id={`storeTools-${toolName}`}
                                        // _navigateToTool dereferences tool.toolName unguarded.
                                        disabled={!tool}
                                        onClick={() => tool && _navigateToTool(tool, toggleMenu, toggleMenuButton)}
                                    >
                                        <Column
                                            spacing="300"
                                            spacingInset="300 200"
                                            alignmentHorizontal="center"
                                        >
                                            <ToolIcon src={tool?.icon} label={label} size={bottomNavConfig.TOOL_ICON_SIZE} />
                                            <Text type="b100" alignment="center">{label}</Text>
                                        </Column>
                                    </button>
                                )
                            })}
                        </Row>
                    ))}
                </Column>
            </Column>
        </div>
    )
}

export default StoreToolsPage
